// app/components/Connectors/binance/OrderBook.tsx:
'use client'

import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react'
import styles from './OrderBook.module.css'
import { throttle } from 'lodash' // Ensure lodash.throttle is installed
import { usePathname } from 'next/navigation'
import {
  ChevronDownIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { formatPrice, formatLargeNumber } from '../utils/priceFormatter'

// --------------------------
// Type Declarations
// --------------------------
interface OrderItem {
  price: number
  size: number
}

interface AggregatedOrderItem {
  price: number
  size: number
  count: number
}

export interface OrderBookData {
  bids: OrderItem[]
  asks: OrderItem[]
}

// --------------------------
// OrderBookInner Component
// --------------------------
// This component handles the live data via WebSocket and renders the order book.
// IMPORTANT: It receives a key based on the symbol so that it fully remounts when the symbol changes.
interface OrderBookInnerProps {
  symbol: string
  stepSize: number
}

const OrderRow = memo(function OrderRow({
  ask,
  widthPercent,
  isAsk,
}: {
  ask: AggregatedOrderItem;
  widthPercent: number;
  isAsk: boolean;
}) {
  return (<div
    className={styles.orderRow}
    title={`Price: ${ask.count > 0 ? formatPrice(ask.price) : '-'}, Size: ${ask.count > 0 ? ask.size.toFixed(2) : '-'}, Total: ${ask.count > 0 ? formatLargeNumber(ask.price * ask.size) : '-'}, Orders: ${ask.count > 0 ? ask.count : '-'}`}
  >
    <div className={`${styles.backgroundBar} ${isAsk ? styles.askBar : styles.bidBar}`} style={{ width: `${widthPercent}%` }}></div>
    <div className={styles.priceText}>{ask.count > 0 ? formatPrice(ask.price) : '-'}</div>
    <div className={styles.amountText}>{ask.count > 0 ? formatLargeNumber(ask.price * ask.size) : '-'}</div>
  </div>);
});

const OrderBookInner = memo(function OrderBookInner({ symbol, stepSize }: OrderBookInnerProps) {
  // State for local order book following Binance's recommended approach
  const [orderBook, setOrderBook] = useState<{
    lastUpdateId: number
    bids: Map<string, number>
    asks: Map<string, number>
  }>({
    lastUpdateId: 0,
    bids: new Map(),
    asks: new Map()
  })

  // State for aggregated data (to render)
  const [aggregatedData, setAggregatedData] = useState<{
    bids: AggregatedOrderItem[]
    asks: AggregatedOrderItem[]
  }>({ bids: [], asks: [] })

  // Buffer for events received before snapshot
  const eventBuffer = useRef<any[]>([])
  const isInitialized = useRef(false)
  const firstEventU = useRef<number | null>(null)

  // Refs to manage scroll positions
  const asksListRef = useRef<HTMLDivElement>(null)
  const bidsListRef = useRef<HTMLDivElement>(null)

  // --------------------------
  // Order Book Management Functions
  // --------------------------
  const getSnapshot = useCallback(async () => {
    try {
      const response = await fetch(`https://api.binance.com/api/v3/depth?symbol=${symbol.toUpperCase()}&limit=1000`)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching snapshot:', error)
      return null
    }
  }, [symbol])

  const applyUpdate = useCallback((update: [string, string], side: 'bids' | 'asks', bookMap: Map<string, number>) => {
    const [price, quantity] = update
    const qty = parseFloat(quantity)
    
    if (qty === 0) {
      // Remove price level
      bookMap.delete(price)
    } else {
      // Update price level
      bookMap.set(price, qty)
    }
  }, [])

  const processEvent = useCallback((event: any) => {
    setOrderBook(prev => {
      // Check update sequence according to Binance guidelines
      if (event.u < prev.lastUpdateId) {
        // Event is older than our local state, ignore
        return prev
      }

      if (event.U > prev.lastUpdateId + 1) {
        // Gap detected, need to resync
        console.error('Order book out of sync, resyncing...')
        isInitialized.current = false
        eventBuffer.current = []
        firstEventU.current = null
        return { lastUpdateId: 0, bids: new Map(), asks: new Map() }
      }

      // Apply updates
      const newBids = new Map(prev.bids)
      const newAsks = new Map(prev.asks)

      event.b.forEach((bid: [string, string]) => applyUpdate(bid, 'bids', newBids))
      event.a.forEach((ask: [string, string]) => applyUpdate(ask, 'asks', newAsks))

      return {
        lastUpdateId: event.u,
        bids: newBids,
        asks: newAsks
      }
    })
  }, [applyUpdate])

  // --------------------------
  // WebSocket Connection Effect
  // --------------------------
  useEffect(() => {
    let ws: WebSocket | null = null
    let reconnectTimer: NodeJS.Timeout | null = null
    let reconnectAttempts = 0
    let isConnected = false

    const initializeOrderBook = async () => {
      // Get snapshot
      const snapshot = await getSnapshot()
      if (!snapshot) {
        console.error('Failed to get snapshot')
        return
      }

      // Check if snapshot is valid compared to buffered events
      if (firstEventU.current !== null && snapshot.lastUpdateId < firstEventU.current) {
        // Snapshot is too old, try again
        setTimeout(initializeOrderBook, 100)
        return
      }

      // Initialize order book from snapshot
      const bidsMap = new Map<string, number>()
      const asksMap = new Map<string, number>()

      snapshot.bids.forEach(([price, qty]: [string, string]) => {
        bidsMap.set(price, parseFloat(qty))
      })

      snapshot.asks.forEach(([price, qty]: [string, string]) => {
        asksMap.set(price, parseFloat(qty))
      })

      setOrderBook({
        lastUpdateId: snapshot.lastUpdateId,
        bids: bidsMap,
        asks: asksMap
      })

      // Process buffered events
      const validEvents = eventBuffer.current.filter(event => event.u > snapshot.lastUpdateId)
      validEvents.forEach(event => processEvent(event))

      // Clear buffer and mark as initialized
      eventBuffer.current = []
      isInitialized.current = true
    }

    const connectWebSocket = () => {
      if (isConnected) return

      try {
        // Use @depth stream for differential updates as per Binance docs
        ws = new WebSocket(
          `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@depth`
        )

        ws.onopen = () => {
          console.log('WebSocket connected for symbol:', symbol)
          reconnectAttempts = 0
          isConnected = true
          
          // Reset state on new connection
          isInitialized.current = false
          eventBuffer.current = []
          firstEventU.current = null
        }

        ws.onmessage = event => {
          try {
            const data = JSON.parse(event.data)
            
            // Record first event U if not set
            if (firstEventU.current === null) {
              firstEventU.current = data.U
            }

            if (!isInitialized.current) {
              // Buffer events until initialized
              eventBuffer.current.push(data)
              
              // Start initialization process if not started
              if (eventBuffer.current.length === 1) {
                initializeOrderBook()
              }
            } else {
              // Process event directly
              processEvent(data)
            }
          } catch (error) {
            console.error('Error parsing data:', error)
          }
        }

        ws.onerror = err => {
          console.error('WebSocket error:', err)
          isConnected = false
        }

        ws.onclose = () => {
          isConnected = false
          if (reconnectTimer) clearTimeout(reconnectTimer)
          const delay = Math.min(10000, 1000 * Math.pow(2, reconnectAttempts))
          reconnectTimer = setTimeout(() => {
            reconnectAttempts++
            connectWebSocket()
          }, delay)
        }
      } catch (error) {
        console.error('WebSocket connection error:', error)
      }
    }

    connectWebSocket()

    return () => {
      if (ws) {
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.close(1000, 'Symbol changed')
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
    }
  }, [symbol, getSnapshot, processEvent])

  // --------------------------
  // Data Aggregation Effect
  // --------------------------
  useEffect(() => {
    // Convert Map to array for aggregation
    const bidsArray: OrderItem[] = Array.from(orderBook.bids.entries()).map(([price, size]) => ({
      price: parseFloat(price),
      size
    }))
    
    const asksArray: OrderItem[] = Array.from(orderBook.asks.entries()).map(([price, size]) => ({
      price: parseFloat(price),
      size
    }))

    // This function aggregates orders based on the selected step size,
    // mimicking the behavior of major exchanges like Binance.
    const aggregateOrders = (orders: OrderItem[], isBids: boolean): AggregatedOrderItem[] => {
      const smallestTickSize = 0.01; // Assuming 0.01 is the most granular tick size for the symbol
      
      // For the smallest step size, we don't need to aggregate further,
      // as the data from Binance is already granular.
      if (stepSize <= smallestTickSize) {
        return orders.map(order => ({
          price: order.price,
          size: order.size,
          count: 1, // Each item from the stream is a single price level
        }));
      }

      const aggregated: Record<string, { size: number; count: number }> = {};
      // Determine the required precision for the price based on the step size
      const precision = stepSize < 1 ? (stepSize.toString().split('.')[1] || '').length : 0;

      orders.forEach(order => {
        // For bids, we round down (floor). For asks, we round up (ceil).
        // This prevents orders from crossing the spread when aggregated.
        // e.g., with step=1, a bid at 140.9 -> '140', and an ask at 140.1 -> '141'.
        let roundedPrice;
        if (isBids) {
            roundedPrice = Math.floor(order.price / stepSize) * stepSize;
        } else {
            roundedPrice = Math.ceil(order.price / stepSize) * stepSize;
        }

        // Using toFixed() to create a string key avoids floating-point inaccuracies.
        const priceKey = roundedPrice.toFixed(precision);

        if (aggregated[priceKey]) {
          aggregated[priceKey].size += order.size;
          aggregated[priceKey].count += 1; // Increment count for each original order
        } else {
          aggregated[priceKey] = { size: order.size, count: 1 };
        }
      });

      return Object.entries(aggregated).map(([priceKey, { size, count }]) => ({
        price: parseFloat(priceKey),
        size,
        count
      }));
    };

    // Aggregate bids and asks separately with the correct rounding strategy.
    let aggregatedBids = aggregateOrders(bidsArray, true).sort(
      (a, b) => b.price - a.price
    );
    let aggregatedAsks = aggregateOrders(asksArray, false).sort(
      (a, b) => a.price - b.price
    );
    const LIMIT = 10;
    // HELP: Limit both sides to the same number of rows.
    const minLength = Math.min(
      aggregatedBids.length,
      aggregatedAsks.length,
      LIMIT
    );
    aggregatedBids = aggregatedBids.slice(0, minLength);
    aggregatedAsks = aggregatedAsks.slice(0, minLength);
    // HELP: Pad with empty entries if needed.
    const padList = (
      list: AggregatedOrderItem[],
      limit: number
    ): AggregatedOrderItem[] => {
      const padded = [...list];
      while (padded.length < limit) {
        padded.push({ price: 0, size: 0, count: 0 });
      }
      return padded;
    };
    aggregatedBids = padList(aggregatedBids, LIMIT);
    aggregatedAsks = padList(aggregatedAsks, LIMIT);

    setAggregatedData({ bids: aggregatedBids, asks: aggregatedAsks });

    // HELP: Optionally scroll the asks section to the bottom.
    if (asksListRef.current) {
      asksListRef.current.scrollTop = asksListRef.current.scrollHeight;
    }
  }, [orderBook, stepSize]);

  // Determine maximum sizes for background bar scaling.
  const maxBidSize = useMemo(() => Math.max(...aggregatedData.bids.map(bid => bid.size), 1), [aggregatedData.bids]);
  const maxAskSize = useMemo(() => Math.max(...aggregatedData.asks.map(ask => ask.size), 1), [aggregatedData.asks]);

  return (
    <div className={styles.mainContent}>
      {/* RENDER ASKS */}
      <div className={styles.asksSection} ref={asksListRef}>
        {aggregatedData.asks.map((ask, idx) => {
          const widthPercent = (ask.size / maxAskSize) * 100
          return <OrderRow
            key={idx}
            ask={ask}
            widthPercent={widthPercent}
            isAsk={true}
          />
        })}
      </div>

      {/* Separator with Best Ask/Bid */}
      <div className={styles.separatorContainer}>
        <div className={styles.bestAsk}>
          {Array.from(orderBook.asks.keys()).length > 0
            ? formatPrice(Math.min(...Array.from(orderBook.asks.keys()).map(price => parseFloat(price))))
            : 'N/A'}
        </div>
        <div className={styles.separator}></div>
        <div className={styles.bestBid}>
          {Array.from(orderBook.bids.keys()).length > 0
            ? formatPrice(Math.max(...Array.from(orderBook.bids.keys()).map(price => parseFloat(price))))
            : 'N/A'}
        </div>
      </div>

      {/* RENDER BIDS */}
      <div className={styles.bidsSection} ref={bidsListRef}>
        {aggregatedData.bids.map((bid, idx) => {
          const widthPercent = (bid.size / maxBidSize) * 100
          return <OrderRow
            key={idx}
            ask={bid}
            widthPercent={widthPercent}
            isAsk={false}
          />
        })}
      </div>
    </div>
  )
})

// --------------------------
// Parent OrderBook Component
// --------------------------
// This component manages the symbol selector and step‑size controls.
// It renders OrderBookInner with a key tied to the current symbol so that a change forces a remount.
export default function OrderBook() {
  const pathname = usePathname()
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'btcusdt'
  // HELP: If the symbol ends with .NS or .BS, use a fallback symbol.
  const symbolToUse =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? 'btcusdt'
      : symbolFromPath

  // State for symbol selection and custom input.
  const [currentSymbol, setCurrentSymbol] = useState(symbolToUse)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'BTCUSDT',
    'ETHUSDT',
    'BNBUSDT',
    'SOLUSDT',
    'LTCUSDT'
  ])
  // State for step‑size.
  const [stepSize, setStepSize] = useState<number>(0.01)

  const handleSymbolChange = useCallback((newSymbol: string) => {
    const formattedSymbol = newSymbol.toUpperCase()
    setCurrentSymbol(formattedSymbol)
    setIsSymbolPopupOpen(false)
    setSymbolInput('')
  }, [])

  return (
    <div className={styles.orderBookContainer}>
      {/* Header with Title and Controls */}
      <div className={styles.header}>
        <span className={styles.title}>Order Book</span>
        <div className={styles.controls}>
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <select
              value={currentSymbol}
              onChange={e => {
                if (e.target.value === 'custom') {
                  setIsSymbolPopupOpen(true)
                } else {
                  handleSymbolChange(e.target.value)
                }
              }}
              aria-label="Select Symbol"
              className={styles.symbolSelect}
            >
              <option value={currentSymbol}>
                {currentSymbol.toUpperCase()}
              </option>
              {popularSymbols.map(
                symbol =>
                  symbol !== currentSymbol.toUpperCase() && (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  )
              )}
              <option value="custom">Custom</option>
            </select>
            {isSymbolPopupOpen && (
              <div className={styles.customSymbolPopup}>
                <input
                  type="text"
                  placeholder="Enter symbol (e.g. BTCUSDT)"
                  value={symbolInput}
                  onChange={e => setSymbolInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && symbolInput.trim()) {
                      handleSymbolChange(symbolInput.trim())
                    }
                  }}
                  className={styles.customSymbolInput}
                />
                <div className={styles.customSymbolButtons}>
                  <button
                    onClick={() => setIsSymbolPopupOpen(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (symbolInput.trim()) {
                        handleSymbolChange(symbolInput.trim())
                      }
                    }}
                    className={styles.applyButton}
                    disabled={!symbolInput.trim()}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Range Filter */}
          <div className={styles.rangeFilter}>
            <select
              id="stepSize"
              value={stepSize}
              onChange={e => setStepSize(Number(e.target.value))}
              aria-label="Select Price Step"
            >
              <option value={0.01}>0.01</option>
              <option value={0.1}>0.1</option>
              <option value={1}>1</option>
              <option value={10}>10</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
      {/* 
          Using key={currentSymbol} forces a full remount of OrderBookInner
          when the symbol changes, so that no stale data persists.
      */}
      <OrderBookInner
        key={currentSymbol}
        symbol={currentSymbol}
        stepSize={stepSize}
      />
    </div>
  )
}
